import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
    Lightbulb, AlertTriangle, AlertOctagon, CheckCircle,
    Calendar, FlaskConical, TrendingUp, TrendingDown, Loader2
} from 'lucide-react';
import { apiRequest } from '../lib/utils';

const ICON_MAP = {
    alert: AlertOctagon,
    check: CheckCircle,
    calendar: Calendar,
    flask: FlaskConical,
    'trend-up': TrendingUp,
    'trend-down': TrendingDown,
    warning: AlertTriangle
};

const SEVERITY_STYLE = {
    critical: 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
    info: 'bg-sky-500/10 border-sky-500/30 text-sky-600 dark:text-sky-400'
};

export default function AnalysisInsights() {
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        apiRequest('/analysis/insights')
            .then((d) => { if (mounted) setInsights(d.insights || []); })
            .catch(() => { if (mounted) setInsights([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <Card className="glass rounded-3xl">
                <CardContent className="py-8 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    if (insights.length === 0) return null;

    return (
        <Card className="glass rounded-3xl" data-testid="analysis-insights">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-primary" />
                    Insights personnalisés
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {insights.map((insight, idx) => {
                    const Icon = ICON_MAP[insight.icon] || Lightbulb;
                    const style = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.info;
                    return (
                        <div
                            key={idx}
                            className={`p-4 rounded-xl border flex items-start gap-3 ${style}`}
                            data-testid={`insight-${idx}`}
                        >
                            <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm mb-0.5">{insight.title}</h4>
                                <p className="text-sm opacity-90">{insight.message}</p>
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}
